import { Band, BandStack } from '@/components/ui/Band'
import { ButtonLink } from '@/components/ui/Button'
import { PageHero } from '@/components/ui/PageHero'
import { Eyebrow, SourceRef } from '@/components/ui/Section'
import { Stagger } from '@/components/motion/Reveal'
import { useDocumentMeta } from '@/lib/seo'
import { ROUTES } from '@/lib/site'
import { ACCREDITATIONS } from '@/content/shared'

const TOTAL_LAYERS = 6

const FACTS: [string, string][] = [
  ['Company', 'GodEngine. One brand, no alternates.'],
  ['Founder', 'Divyaprakash Jha, operating as FORGE X. Solo founder.'],
  ['Base', 'India. INR primary, USD secondary.'],
  ['Self-description', 'An SME. Not a scale-up, and not described as one.'],
  [
    'Platform',
    'The web platform is live and currently free to all users; billing meters are deliberately off.',
  ],
  [
    'Other surfaces',
    'A newsroom vertical and an intelligence desk vertical, alongside the platform.',
  ],
  ['This product', 'Phase 0 complete. Not publicly available.'],
]

const OPEN_ITEMS = [
  {
    name: 'Registered entity details',
    detail: 'Recorded internally as an open item rather than approximated in public.',
  },
  {
    name: 'User and revenue counts',
    detail: 'To be exported from production rather than estimated.',
  },
  { name: 'Programme acceptance letters', detail: 'Held, to be filed for diligence.' },
  {
    name: 'First design partner',
    detail: 'Open. Which sections get depth first depends on who it is and what data they hold.',
  },
]

const PRACTICES = [
  {
    name: 'Read before write',
    detail:
      'A new finding that contradicts memory does not overwrite it. Both are kept, the contradiction is recorded, and the better-evidenced one is preferred.',
  },
  {
    name: 'Audits are an asset',
    detail:
      'The repository contains adversarial self-audits that contradict earlier company marketing. They are presented rather than buried, and the audited figure supersedes the marketing one everywhere.',
  },
  {
    name: 'Mechanisms stay internal',
    detail:
      'Capability and outcome are described publicly. Scoring formulas, algorithm bodies and orchestration internals are not, on this page or anywhere else outward-facing.',
  },
]

export default function Company() {
  useDocumentMeta(ROUTES.company)

  return (
    <BandStack>
      <Band layer={1} total={TOTAL_LAYERS} tone="ice" routeTab="company">
        <PageHero
          crumb="Company"
          title="A small company that measured itself harder than it had to."
          lede="GodEngine builds decision software. The Business Intelligence Simulator is its newest surface, and the discipline it applies to your figures is the same one applied to the sentences on this page: every claim carries a tag, and an untagged claim is treated as a defect."
        />
      </Band>

      <Band layer={2} total={TOTAL_LAYERS} tone="warm" labelledBy="facts-heading">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Eyebrow className="text-primary">Facts</Eyebrow>
            <h2
              id="facts-heading"
              className="mt-6 text-balance text-[28px] font-extrabold leading-[1.13] tracking-[-0.025em] sm:text-[36px]"
            >
              The whole of it, in one column.
            </h2>
            <p className="mt-5 max-w-measure text-[15px] leading-[1.75] text-muted">
              Solo-founded and effectively a single-author codebase. That is a real constraint and
              it is stated rather than obscured behind a team page of advisers.
            </p>
          </div>
          <div>
            <dl className="grid gap-3 card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card sm:p-7">
              {FACTS.map(([term, detail]) => (
                <div
                  key={term}
                  className="grid gap-1 border-b border-line pb-3 last:border-b-0 last:pb-0 sm:grid-cols-[150px_1fr] sm:gap-4"
                >
                  <dt className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-primary">
                    {term}
                  </dt>
                  <dd className="text-[13.5px] leading-[1.65] text-muted">{detail}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-4">
              <SourceRef>Company facts: [founder] · product status: [repo]</SourceRef>
            </div>
          </div>
        </div>
      </Band>

      <Band layer={3} total={TOTAL_LAYERS} tone="ice" labelledBy="validation-heading">
        <Eyebrow className="text-primary">Third-party validation</Eyebrow>
        <h2
          id="validation-heading"
          className="mt-6 max-w-3xl text-balance text-[28px] font-extrabold leading-[1.13] tracking-[-0.025em] sm:text-[38px]"
        >
          Three programme acceptances. That is the complete list.
        </h2>
        <p className="mt-5 max-w-measure text-[15px] leading-[1.75] text-muted">
          Documentation is available for each. They are founder-attested here because the letters
          are not published, and they are the strongest external validation the company currently
          has.
        </p>
        <Stagger
          variant="up"
          step={100}
          as="ul"
          itemAs="li"
          className="mt-10 grid gap-4 sm:grid-cols-3"
        >
          {ACCREDITATIONS.map((item) => (
            <div
              key={item.name}
              className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card"
            >
              <h3 className="text-[16px] font-extrabold tracking-[-0.015em]">{item.name}</h3>
              <p className="mt-2 text-[13.5px] leading-[1.7] text-muted">
                {item.name === 'Microsoft for Startups' ? 'Founders Hub. Accepted.' : 'Accepted.'}
              </p>
              <p className="mt-4 font-mono text-[11px] text-muted-soft">{item.tag}</p>
            </div>
          ))}
        </Stagger>
        <div className="mt-6 rounded-card border border-line bg-white/70 backdrop-blur p-6 sm:p-7">
          <h3 className="text-[16px] font-extrabold tracking-[-0.015em]">
            A correction, kept in public
          </h3>
          <p className="mt-3 max-w-measure text-[14px] leading-[1.75] text-muted">
            An earlier marketing surface claimed four programme acceptances, including one with a
            dollar figure attached. The fourth is unattested, so it does not appear here and will
            not until it is confirmed or removed at the source. Publishing the correction is cheaper
            than being caught by it.
          </p>
        </div>
      </Band>

      <Band layer={4} total={TOTAL_LAYERS} tone="mint" labelledBy="absent-heading">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Eyebrow className="text-primary">What we do not have</Eyebrow>
            <h2
              id="absent-heading"
              className="mt-6 text-balance text-[27px] font-extrabold leading-[1.14] tracking-[-0.025em] sm:text-[34px]"
            >
              No customers to cite, no revenue to quote, no press to link.
            </h2>
            <p className="mt-5 max-w-measure text-[15px] leading-[1.75] text-muted">
              There are no evidenced customers, no evidenced revenue, no evidenced retention and no
              evidenced press. Anything on this site that looks like traction is one of the three
              programme acceptances or the fact that the platform is live and free.
            </p>
            <p className="mt-4 max-w-measure text-[15px] leading-[1.75] text-muted">
              The alternative — a logo wall of companies that once opened a demo, or a growth chart
              with no axis labels — is the thing this whole product exists to argue against. It
              would be strange to build it into our own marketing.
            </p>
          </div>
          <Stagger variant="up" step={80} as="ul" itemAs="li" className="grid gap-3">
            {OPEN_ITEMS.map((item) => (
              <div
                key={item.name}
                className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-5 shadow-card"
              >
                <strong className="block text-[14.5px] font-bold tracking-[-0.01em]">
                  {item.name}
                </strong>
                <span className="mt-1.5 block text-[13.5px] leading-[1.7] text-muted">
                  {item.detail}
                </span>
              </div>
            ))}
          </Stagger>
        </div>
      </Band>

      <Band layer={5} total={TOTAL_LAYERS} tone="warm" labelledBy="practice-heading">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Eyebrow className="text-primary">How we work</Eyebrow>
            <h2
              id="practice-heading"
              className="mt-6 text-balance text-[28px] font-extrabold leading-[1.13] tracking-[-0.025em] sm:text-[36px]"
            >
              Memory first, artifacts second.
            </h2>
            <p className="mt-5 max-w-measure text-[15px] leading-[1.75] text-muted">
              Decisions, findings and honest assessments are written into a tracked organisational
              memory as they happen. Everything external — a deck, a memo, this website — is
              generated from it rather than written free-hand, and anything newly derived is written
              back.
            </p>
            <p className="mt-4 max-w-measure text-[15px] leading-[1.75] text-muted">
              Practical consequence: the copy on this page and the code in the repository cannot
              drift apart quietly, because one is derived from the other.
            </p>
          </div>
          <Stagger variant="up" step={90} as="ul" itemAs="li" className="grid gap-4">
            {PRACTICES.map((item) => (
              <div
                key={item.name}
                className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card"
              >
                <h3 className="text-[16px] font-extrabold tracking-[-0.015em]">{item.name}</h3>
                <p className="mt-3 text-[13.5px] leading-[1.7] text-muted">{item.detail}</p>
              </div>
            ))}
          </Stagger>
        </div>
      </Band>

      <Band layer={6} total={TOTAL_LAYERS} tone="ice" labelledBy="company-cta" showMark={false}>
        <div className="mx-auto max-w-3xl text-center">
          <h2
            id="company-cta"
            className="text-balance text-[26px] font-extrabold leading-[1.14] tracking-[-0.025em] sm:text-[34px]"
          >
            If the honesty is the part you find interesting, we should talk.
          </h2>
          <p className="mx-auto mt-5 max-w-[56ch] text-pretty text-[15px] leading-[1.75] text-muted">
            Design partners, technical diligence, or a question about anything stated on this site.
          </p>
          <div className="mt-9 flex justify-center">
            <ButtonLink to={ROUTES.contact.path} size="lg">
              Get in touch
            </ButtonLink>
          </div>
        </div>
      </Band>
    </BandStack>
  )
}
